import * as Accordion from "@radix-ui/react-accordion";
import classNames from "classnames";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { ConditionalLink } from "components/conditionalLink";
import { Badge } from "components/design/badge";
import { ArrowRight } from "components/icons/ArrowRight";
import { EmptyIcon } from "components/icons/EmptyIcon";
import { Cross } from "components/icons/Cross";
import { PlusIcon } from "components/icons/PlusIcon";
import { ARCHIVED } from "constants/task";
import dayjs from "dayjs";
import { TASK_STATE_TO_COLOR } from "dictionaries";
import { chatInviteValidation } from "formValidations/chatInviteInvalidation";
import { Form, Formik, FormikValues, useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import SmallWhatsappIcon from "static/images/SmallWhatsappIcon.svg";
import {
  useGetAllSharedMessageQuery,
  useGetMembersOfTeamForChannelQuery,
  useGetTeamsForChannelQuery,
  useInviteTeamOrMemberInChannelMutation,
} from "store/apis/chat";
import { useGetTaskFromChannelIdQuery, useGetTaskQuery } from "store/apis/task";
import {
  openShareViaWhatsappSlider,
  setChannelInfoSliderActive,
  setScrollToAuditTrail,
} from "store/slices/channelInfoSlider";
import { expandDashboardSidebar } from "store/slices/dashboard";
import { RootState } from "store/store";
import { ChannelMemberResponse } from "stream-chat";
import {
  DefaultStreamChatGenerics,
  useChannelPreviewInfo,
  useChannelStateContext,
} from "stream-chat-react";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { ActionCentre } from "./ChannelInfo/ActionCentre/ActionCentre";
import { ChatTickets } from "./ChannelInfo/ChatTickets/ChatTickets";
import { MediaDocuments } from "./ChannelInfo/MediaDocuments/MediaDocuments";
import { ChannelInfoWrapper } from "./ChannelInfo/ChannelInfoWrapper";
import { CaretDown } from "components/icons/CaretDown";
import { Accordion as CustomAccordion } from "components/DesignSystem/Accordion/Accordion";
import {
  TeamMembersForCustomer,
  ROLE_TAG_TYPE,
  TeamMember,
} from "./ChannelInfo/TeamMembers/TeamMembers";
import { Tag as RoleTag } from "components/DesignSystem/Tag/Tag";
import * as CHANNEL_TYPES from "constants/channelType";
import { DISPLAY_DATE } from "constants/date";
import { TagCombobox } from "components/TagCombobox/TagCombobox";
import Tag from "components/design/Tag";
import { FilingAndRegistrationInfo } from "./FilingAndRegistrationInfo";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import ChannelAuditTrail from "./ChannelInfo/ChannelAuditTrail/ChannelAuditTrail";
import {
  ACCOUNT_MANAGER,
  BOOKKEEPER,
  PREPARER,
  REVIEWER,
} from "constants/chatMemberType";

export const InfoAccordion = ({
  members,
  title,
}: {
  members: ChannelMemberResponse<DefaultStreamChatGenerics>[];
  title: string;
}) => {
  return (
    <Accordion.Root type="single" collapsible>
      <Accordion.Item value={title || "CompanyGroup"}>
        <Accordion.Trigger asChild>
          <div className="t-group t-w-full t-h-full t-flex t-p-2 t-items-center t-justify-between t-pl-0">
            <div className="t-flex t-items-center t-gap-2 t-h-full">
              <div
                className={classNames(
                  "group-data-state-open:t-rotate-0 group-data-state-closed:-t-rotate-90 t-text-text-30"
                )}
              >
                <CaretDown className="t-h-3 t-w-3" />
              </div>
              <div className="t-text-body-sm t-h-full t-text-text-30">
                {title || "Company Group"} Team
              </div>
            </div>
          </div>
        </Accordion.Trigger>
        <Accordion.Content>
          <div className="t-flex t-flex-col t-gap-3 t-p-2">
            {Object.entries(members).map(([memberId, member]) => {
              return (
                <div className="t-flex t-items-center t-gap-2" key={memberId}>
                  <Avatar
                    lettersCount={2}
                    size="regular"
                    src={member?.user?.image || ""}
                    alt={member?.user?.name || ""}
                    className="t-w-8 t-h-8 t-rounded-full"
                  />
                  <p className="t-m-0 t-text-subtext">
                    {member?.user?.name || ""}
                  </p>
                </div>
              );
            })}
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export const InfoItem = ({
  title,
  value,
}: {
  title: string;
  value: ReactNode;
}) => {
  return (
    <div className="t-space-y-1.5">
      <p className="t-text-caption t-text-text-30 t-m-0">{title}</p>
      {value}
    </div>
  );
};

const TaskLink = () => {
  const { channel } = useChannelStateContext();
  const { data: currentChannelData } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
    }
  );
  const { displayTitle } = useChannelPreviewInfo({
    channel,
  });

  if (
    !(
      currentChannelData &&
      currentChannelData?.tasks &&
      currentChannelData?.tasks.length > 0
    )
  ) {
    return null;
  }

  if (currentChannelData.tasks.length > 1) {
    return (
      <InfoItem
        title="Filings"
        value={
          <div className="t-flex t-gap-2 t-flex-col">
            <div>
              <p className="t-m-0 t-text-body-sm">{displayTitle}</p>
              <p className="t-m-0 t-text-body-sm t-text-text-30">
                Includes {currentChannelData.tasks.length} filings
              </p>
            </div>
            {currentChannelData.tasks.map((task) => {
              const taskStateType = task?.is_archived
                ? ARCHIVED
                : task?.current_state?.type || "Not Started";

              const taskState = task?.is_archived
                ? "Archived"
                : task?.current_state?.name || "Not Started";

              return (
                <div className="t-flex t-justify-between" key={task.uuid}>
                  <ConditionalLink
                    to={`/filings/${task?.uuid}`}
                    className="t-text-body-sm t-text-purple hover:!t-underline t-truncate t-w-3/5 t-text-ellipsis"
                  >
                    {task.name}
                  </ConditionalLink>
                  <div className="t-flex-1 t-shrink-0 t-w-2/5 t-flex t-justify-end">
                    <Badge
                      color={TASK_STATE_TO_COLOR[taskStateType]}
                      size="small"
                    >
                      {taskState}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        }
      />
    );
  }

  const task = currentChannelData?.tasks[0];

  const taskStateType = task?.is_archived
    ? ARCHIVED
    : task?.current_state?.type || "Not Started";

  const taskState = task?.is_archived
    ? "Archived"
    : task?.current_state?.name || "Not Started";

  return (
    <>
      <InfoItem
        title="Filing"
        value={
          <ConditionalLink
            to={`/filings/${task?.uuid}`}
            className="t-text-body-sm t-text-purple hover:!t-underline"
          >
            {task.name}
          </ConditionalLink>
        }
      />
      {(task?.current_state || task?.is_archived) && (
        <InfoItem
          title="Step"
          value={
            <span className="t-flex">
              <Badge color={TASK_STATE_TO_COLOR[taskStateType]} size="small">
                {taskState}
              </Badge>
            </span>
          }
        />
      )}
    </>
  );
};

export const Info = () => {
  const { channel } = useChannelStateContext();
  const { isCustomer } = useRoleBasedView();
  const dispatch = useDispatch();
  const { isAdmin } = useRoleBasedView();
  const { data: currentChannelData, isLoading } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
    }
  );
  const groupId = currentChannelData?.company_group?.uuid;
  const task = currentChannelData?.tasks[0];

  const { data: taskDetail } = useGetTaskQuery(
    { taskId: task?.uuid! },
    { skip: !task?.uuid, refetchOnMountOrArgChange: true }
  );

  const { data: allSharedMessage } = useGetAllSharedMessageQuery(
    { groupId: groupId!, channelId: channel.id! },
    { skip: !channel.id || !groupId }
  );

  const entity = currentChannelData?.entity;
  const companyGroup = currentChannelData?.company_group;
  const entityLink = isCustomer ? `/entities` : `/crm/${companyGroup?.uuid}`;

  const hasContent =
    Boolean(isAdmin && companyGroup?.tags?.length) ||
    Boolean(entity) ||
    Boolean(task) ||
    Boolean(isAdmin && allSharedMessage?.forwarded_messages_count) ||
    Boolean(isAdmin && taskDetail?.assigned_team?.name) ||
    Boolean(taskDetail?.assigned_to?.name);

  const openAllSharedMessages = () => {
    dispatch(openShareViaWhatsappSlider());
  };

  return (
    <CustomAccordion.Root
      type="single"
      className="t-flex t-flex-col t-w-full"
      collapsible
      defaultValue={isAdmin && "DETAILS"}
    >
      <CustomAccordion.Item value="DETAILS">
        <CustomAccordion.Trigger>Channel Details</CustomAccordion.Trigger>
        <CustomAccordion.Content className="t-space-y-4">
          {isAdmin && companyGroup?.tags && companyGroup?.tags.length > 0 && (
            <InfoItem
              title="Group Tags"
              value={
                <div className="t-flex t-flex-wrap t-gap-2">
                  {companyGroup.tags.map((tag) => (
                    <Tag
                      size="small"
                      key={tag.uuid}
                      title={tag.title}
                      bg_color_hex={tag.bg_color_hex}
                      color_hex={tag.color_hex}
                    />
                  ))}
                </div>
              }
            />
          )}

          {entity && (
            <InfoItem
              title="Entity"
              value={
                <ConditionalLink
                  to={entityLink}
                  className="t-flex t-text-body-sm after:t-ml-2 t-text-text-100 hover:t-text-purple hover:!t-underline t-items-center t-gap-2"
                >
                  <ReactCountryFlag
                    style={{
                      width: "14px",
                      height: "10px",
                    }}
                    svg
                    countryCode={entity.code_alpha_2}
                  />
                  <span className="t-max-w-[120px] t-truncate">
                    {entity.name}
                  </span>
                </ConditionalLink>
              }
            />
          )}

          <TaskLink />

          {task?.deadline && (
            <InfoItem
              title="Deadline"
              // @ts-ignore
              value={
                <p className="t-m-0 t-text-body-sm">
                  {dayjs(task.deadline).format(DISPLAY_DATE)}
                </p>
              }
            />
          )}

          {task?.eligibility && (
            <InfoItem
              title="Eligibility"
              // @ts-ignore
              value={<p className="t-m-0 t-text-body-sm">{task.eligibility}</p>}
            />
          )}
          {isAdmin &&
            Boolean(
              allSharedMessage?.forwarded_messages_count &&
                allSharedMessage?.forwarded_messages_count > 0
            ) && (
              <InfoItem
                title="Shared via Whatsapp"
                value={
                  <p
                    className="t-m-0 t-text-body-sm t-flex t-justify-between t-items-center t-px-2 t-py-1 t-border t-border-solid t-border-neutral-0 t-rounded"
                    onClick={openAllSharedMessages}
                    role="button"
                  >
                    <span className="t-flex t-items-center t-gap-1">
                      <img
                        src={SmallWhatsappIcon}
                        alt="SmallWhatsappIcon"
                        height="16"
                      />
                      {pluralize(
                        allSharedMessage?.forwarded_messages_count as number,
                        "message",
                        "messages"
                      )}{" "}
                      sent
                    </span>

                    <Button customType="ghost_icon" size="xsmall">
                      <ArrowRight />
                    </Button>
                  </p>
                }
              />
            )}
          {isAdmin && taskDetail?.assigned_team?.name && (
            <InfoItem
              title="Assigned team"
              value={
                <p className="t-m-0 t-text-body-sm">
                  {taskDetail.assigned_team.name}
                </p>
              }
            />
          )}
          {taskDetail?.assigned_to?.name && (
            <InfoItem
              title="Preparer"
              value={
                <p className="t-m-0 t-text-body-sm">
                  {taskDetail?.assigned_to?.name}
                </p>
              }
            />
          )}
          {!isLoading && !hasContent && (
            <div className="t-flex t-flex-col t-items-center t-py-5 t-gap-3">
              <EmptyIcon />
              <span className="t-text-subtext-sm t-text-text-30">
                No details available
              </span>
            </div>
          )}
        </CustomAccordion.Content>
      </CustomAccordion.Item>
    </CustomAccordion.Root>
  );
};

export const Team = () => {
  const { channel } = useChannelStateContext();
  const channelCustomType = channel?.data?.custom_type;
  const isBookChannel =
    channelCustomType === CHANNEL_TYPES.BOOKS_CHANNEL_CUSTOM_TYPE;

  const isFilingChannel = channelCustomType === CHANNEL_TYPES.TASK_CHANNEL;
  const isCompanyGroupChannel =
    channelCustomType === CHANNEL_TYPES.COMPANY_GROUP_CUSTOM_TYPE;

  const assignedAgentMemberId = channel?.data
    ?.assigned_agent_member_id as string;
  const assignedPreparerMemberId = channel?.data
    ?.assigned_preparer_member_id as string;
  const assignedReviewerMemberId = channel?.data
    ?.assigned_reviewer_member_id as string;

  const allMembers = useMemo(
    () =>
      (channel?.state?.members ?? {}) as Record<
        string,
        ChannelMemberResponse<DefaultStreamChatGenerics>
      >,
    [channel?.state?.members]
  );

  const teamMembers = useMemo(() => {
    const teamRoles = isCompanyGroupChannel
      ? [ACCOUNT_MANAGER]
      : [
          ACCOUNT_MANAGER,
          ...(isBookChannel ? [BOOKKEEPER] : []),
          ...(isFilingChannel ? [PREPARER, REVIEWER] : []),
        ];

    const members = Object.values(allMembers);
    const membersById = new Map(members.map((m) => [m.user?.id as string, m]));
    const teamMemberMap = new Map<
      string,
      {
        member: ChannelMemberResponse<DefaultStreamChatGenerics>;
        roles: string[];
      }
    >();

    const addRole = (userId: string | undefined, role: string) => {
      const member = userId ? membersById.get(userId) : undefined;
      if (!member) return;
      const existing = teamMemberMap.get(userId!);
      if (existing) {
        existing.roles.push(role);
      } else {
        teamMemberMap.set(userId!, { member, roles: [role] });
      }
    };

    const preparerRole = isBookChannel ? BOOKKEEPER : PREPARER;
    addRole(assignedAgentMemberId, ACCOUNT_MANAGER);
    addRole(assignedPreparerMemberId, preparerRole);
    if (isFilingChannel) addRole(assignedReviewerMemberId, REVIEWER);

    return Array.from(teamMemberMap.entries())
      .map(([userId, { member, roles }]) => ({
        id: userId,
        image: (member.user?.image as string) || "",
        name: (member.user?.name as string) || "",
        roleNames: roles,
      }))
      .sort(
        (a, b) =>
          teamRoles.indexOf(a.roleNames[0]) - teamRoles.indexOf(b.roleNames[0])
      );
  }, [
    allMembers,
    assignedAgentMemberId,
    assignedPreparerMemberId,
    assignedReviewerMemberId,
    isBookChannel,
    isCompanyGroupChannel,
    isFilingChannel,
  ]);

  return (
    <ChannelInfoWrapper
      title={
        <div className="t-text-subtext t-flex t-gap-1 t-items-center">
          Team Members
        </div>
      }
      openDetails={() => {}}
      hasDetails={false}
    >
      <div className="t-flex t-flex-col t-gap-3">
        <div className="t-flex t-flex-col t-gap-3 t-p-2">
          {teamMembers.length === 0 && (
            <div className="t-flex t-flex-col t-items-center t-py-5 t-gap-3">
              <EmptyIcon />
              <span className="t-text-subtext-sm t-text-text-30">
                No team members assigned
              </span>
            </div>
          )}
          {teamMembers.map((member) => (
            <div
              className={classNames(
                { "t-items-start": member.roleNames.length > 1 },
                { "t-items-center": member.roleNames.length <= 1 },
                "t-flex t-gap-2 t-justify-between"
              )}
              key={member.id}
            >
              <div className="t-flex t-items-center t-gap-2">
                <Avatar
                  lettersCount={2}
                  size="regular"
                  src={member.image}
                  alt={member.name}
                  className="t-w-8 t-h-8 t-rounded-full"
                />
                <p className="t-m-0 t-text-subtext">{member.name}</p>
              </div>
              {member.roleNames.length > 0 && (
                <span className="t-flex t-flex-col t-gap-1 t-shrink-0 t-items-end">
                  {member.roleNames.map((role) => (
                    <RoleTag
                      key={role}
                      tagType={ROLE_TAG_TYPE[role] || "gray"}
                      size="small"
                      icon={false}
                    >
                      {role}
                    </RoleTag>
                  ))}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </ChannelInfoWrapper>
  );
};

const MemberInvite = () => {
  const { values } = useFormikContext<{ team: string }>();
  const { channel } = useChannelStateContext();

  const { data: teamMembers = [], isLoading } =
    useGetMembersOfTeamForChannelQuery(
      {
        channelUrl: channel.id!,
        teamId: values?.team,
      },
      { skip: !values?.team || !channel?.id }
    );

  const members = teamMembers.map(
    ({ member: { name }, member_id, is_channel_member }) => ({
      label: name,
      value: member_id,
      isDisabled: is_channel_member,
    })
  );

  return (
    <Combobox
      withForm
      name="members"
      label="Members"
      isMulti
      isLoading={isLoading}
      menuPortalTarget={document.body}
      placeholder="Select members"
      options={members}
    />
  );
};

const InviteTeam = () => {
  const { channel } = useChannelStateContext();

  const { successToast, alertToast } = useToast();

  const inviteModal = useModal();
  const addTeamConfirmationModal = useModal();

  const [inviteTeamOrMember] = useInviteTeamOrMemberInChannelMutation();

  const { data: serviceTeams = [], isLoading } = useGetTeamsForChannelQuery({
    channelUrl: channel.id!,
  });

  const onSubmit = async (values: FormikValues) => {
    try {
      await inviteTeamOrMember({
        groupId: channel?.data?.group_uuid as string,
        channelUrl: channel.id!,
        teamId: values.team,
        memberIds: values.members,
      }).unwrap();
      inviteModal.close();
      addTeamConfirmationModal.close();
      successToast({ message: "" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <>
      <button
        className="all:unset t-box-border t-text-nowrap t-flex t-cursor-pointer t-items-center t-justify-center t-rounded-lg t-p-4 !t-font-medium t-drop-shadow-button t-transition-all disabled:t-pointer-events-none disabled:t-cursor-not-allowed t-text-body-sm t-h-12 t-border t-border-solid t-border-neutral-10 hover:t-bg-surface-lighter-grey t-bg-white focus:t-bg-neutral-0 data-[active=true]:t-bg-neutral-0  disabled:t-bg-neutral-10 t-w-full"
        onClick={inviteModal.open}
      >
        <div className="t-flex t-w-full t-justify-between t-gap-2">
          <div className="t-text-subtext">Invite</div>
          <span className="t-text-text-30 t-mr-1">
            <PlusIcon />
          </span>
        </div>
      </button>
      <Formik
        initialValues={{ type: "NEW_TEAM", team: "" }}
        onSubmit={onSubmit}
        validationSchema={chatInviteValidation}
      >
        {({ values, submitForm, setFieldValue, isSubmitting }) => (
          <Form>
            <Modal.Root
              open={inviteModal.isOpen}
              onOpenChange={inviteModal.close}
              modal={false}
            >
              <Modal.Content useCustomOverlay>
                <Modal.Header>
                  <Modal.Title>Invite</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <div className="t-text-body-sm">
                      <div>Do you want to?</div>
                      <RadioGroup.Root
                        className="t-mb-6"
                        onValueChange={(value) => {
                          if (value === "NEW_TEAM") {
                            setFieldValue("members", null);
                          }
                          setFieldValue("type", value);
                        }}
                        value={values.type}
                      >
                        <div className="t-py-1.5 t-pt-3 t-px-3">
                          <RadioGroup.Item value="NEW_TEAM">
                            <span className="t-text-body-sm t-flex t-items-center t-h-full">
                              Add a new team
                            </span>
                          </RadioGroup.Item>
                        </div>
                        <div className="t-pt-1.5 t-px-3">
                          <RadioGroup.Item value="NEW_MEMBER">
                            <span className="t-flex t-items-center t-h-full">
                              Add members to an existing Team
                            </span>
                          </RadioGroup.Item>
                        </div>
                      </RadioGroup.Root>
                    </div>

                    <Combobox
                      withForm
                      name="team"
                      label="Team"
                      isLoading={isLoading}
                      menuPortalTarget={document.body}
                      placeholder="Select team"
                      options={serviceTeams.map(
                        ({ name, uuid, is_in_channel }) => ({
                          value: uuid,
                          label: name,
                          isDisabled:
                            (is_in_channel && values.type === "NEW_TEAM") ||
                            (!is_in_channel && values.type === "NEW_MEMBER"),
                        })
                      )}
                    />
                    {values.type === "NEW_MEMBER" && <MemberInvite />}
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={() => {
                      values.type === "NEW_TEAM" && values.team
                        ? addTeamConfirmationModal.open()
                        : submitForm();
                    }}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    Next
                  </Button>
                </Modal.FooterButtonGroup>
              </Modal.Content>
            </Modal.Root>
            <Modal.Root
              open={addTeamConfirmationModal.isOpen}
              onOpenChange={addTeamConfirmationModal.close}
            >
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Add a new team</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <div className="t-text-subtitle-sm">
                    Adding team to the channel will provide them access to the
                    filing as well. Are you sure?
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    Confirm
                  </Button>
                </Modal.FooterButtonGroup>
              </Modal.Content>
            </Modal.Root>
          </Form>
        )}
      </Formik>
    </>
  );
};

export const ChannelInfoSlider = ({ block = false }: { block?: boolean }) => {
  const dispatch = useDispatch();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const { channel } = useChannelStateContext();
  const sliderOpen = useSelector(
    (store: RootState) => store.channelInfoSlider.isActive
  );
  const expandSidebarOnMessageSearchClose = useSelector(
    (store: RootState) => store.channelInfoSlider.setSideBarToExpandOnClose
  );
  const scrollToAuditTrail = useSelector(
    (store: RootState) => store.channelInfoSlider.scrollToAuditTrail
  );
  const auditTrailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToAuditTrail && sliderOpen) {
      auditTrailRef.current?.scrollIntoView({ behavior: "smooth" });
      dispatch(setScrollToAuditTrail(false));
    }
  }, [scrollToAuditTrail, sliderOpen, dispatch]);

  const { data: currentChannelData } = useGetTaskFromChannelIdQuery(
    {
      channelId: channel.id!,
    },
    {
      skip: !channel.id,
    }
  );
  const groupId = currentChannelData?.company_group?.uuid;

  const { data: allSharedMessage } = useGetAllSharedMessageQuery(
    { groupId: groupId!, channelId: channel.id! },
    { skip: !channel.id || !groupId }
  );

  const onChannelInfoClose = () => {
    dispatch(setChannelInfoSliderActive({ isActive: false }));
    if (expandSidebarOnMessageSearchClose) {
      dispatch(expandDashboardSidebar());
    }
  };

  return (
    <div
      className={classNames(
        "t-transition-all t-grow t-shrink-0 t-bg-surface t-overflow-x-hidden t-z-0 t-flex t-flex-col t-h-full",
        {
          "t-border-0 t-border-l t-border-solid t-border-neutral-10":
            sliderOpen,
          "!t-w-0": !sliderOpen,
          "t-w-full": block && sliderOpen,
          "!t-w-[360px] sm:!t-w-full": !block && sliderOpen,
        }
      )}
    >
      {sliderOpen && (
        <>
          <div className="t-py-3 t-pr-4 t-pl-5 t-flex t-justify-between t-items-center">
            <span className="t-text-subtitle">Details</span>
            <Button
              customType="ghost_icon"
              size="small"
              onClick={onChannelInfoClose}
            >
              <Cross />
            </Button>
          </div>
          <div className="t-flex t-flex-col t-gap-4 t-px-5 t-pb-5 t-pt-2 t-overflow-y-auto t-flex-1 t-overscroll-contain">
            {isCustomer ? <TeamMembersForCustomer /> : <Team />}
            {isAdmin && <Info />}
            <ActionCentre
              groupId={currentChannelData?.company_group?.uuid || ""}
            />
            {currentChannelData?.entity?.uuid &&
              currentChannelData.tasks[0]?.uuid && (
                <div ref={auditTrailRef}>
                  <ChannelAuditTrail
                    entityId={currentChannelData?.entity?.uuid}
                    objectId={currentChannelData?.tasks?.[0]?.uuid}
                  />
                </div>
              )}
            <ChatTickets />
            <FilingAndRegistrationInfo />
            <MediaDocuments
              groupId={currentChannelData?.company_group?.uuid || ""}
            />

            {(currentChannelData?.entity ||
              currentChannelData?.tasks[0] ||
              Boolean(
                allSharedMessage?.forwarded_messages_count &&
                  allSharedMessage?.forwarded_messages_count > 0
              )) &&
              isCustomer && <Info />}

            {isAdmin && (
              <>
                <hr className="t-m-0 t-text-neutral-20" />
                <InviteTeam />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
